import { usePersistedState } from '../util/persistedState';
import { useAsync } from 'react-use';
import { useEffect } from 'react';
import { PromiseType } from 'react-use/lib/util';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { Duration } from 'date-fns';
import { ConfigHelper } from '@devxdiscover/devhub-common';

/**
 * A single secVuln user data return
 */
export type UserData = {
  data: {
    getUser: {
      login: string;
      secvulnSummaryByLogin: SecuritySummary;
      secvulnSummaryDetailsByLogin: SecuritySummaryDetails[];
    };
  };
  errors: any[];
};

/**
 * A single SecuritySummary
 */
export type SecuritySummary = {
  login: string;
  sp1: string;
  sp2: string;
  sp3: string;
  sp4: string;
};

/**
 * secVuln details
 */
export type SecuritySummaryDetails = {
  vulnerabilityId: string;
  vulnerabilityLink: string;
  securityPriority: string;
  status: string;
};

/**
 * Retrieves the security vulnerabilities from the federated graph.
 * @param cacheDuration - The {@link Duration} after which the state should be considered expired.
 */
export const useSecurityVulns: (cacheDuration?: Duration) => {
  loading: boolean;
  error?: Error;
  value?: PromiseType<Promise<UserData>>;
} = (cacheDuration: Duration = { minutes: 60 }) => {
  const secVulnsOverview = {
    data: {
      getUser: {
        login: '',
        secvulnSummaryByLogin: {
          login: '',
          sp1: '',
          sp2: '',
          sp3: '',
          sp4: '',
        },
        secvulnSummaryDetailsByLogin: [],
      },
    },
    errors: [],
  };

  const [secVulns, setSecVulns] = usePersistedState<UserData>(
    'secVulns',
    cacheDuration,
    secVulnsOverview,
  );

  const identityApi = useApi(identityApiRef);

  async function getBackstageIdentity() {
    return await identityApi.getBackstageIdentity();
  }

  const { value, loading, error } = useAsync(async (): Promise<UserData> => {
    if (!secVulns?.data?.getUser?.secvulnSummaryByLogin?.login) {
      const login = await getBackstageIdentity();
      const patchingMetricsQuery = `
          query GetUser($login: String!) {
              getUser(login: $login) {
                  login
                  secvulnSummaryByLogin {
                      login
                      sp1
                      sp2
                      sp3
                      sp4
                  }
                  secvulnSummaryDetailsByLogin{
                      vulnerabilityId
                      vulnerabilityLink
                      securityPriority
                      status
                  }
              }
          }        
      `;

      const response = await window.fetch(
        ConfigHelper.instance.proxyUrl('/graph/').href,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json;charset=UTF-8',
          },
          body: JSON.stringify({
            query: patchingMetricsQuery,
            variables: {
              login: login.userEntityRef.split('/')[1],
            },
          }),
        },
      );
      const secVul = await response.json();
      secVulnsOverview.data = secVul?.data;
      return secVulnsOverview;
    }
    return secVulns;
  });

  useEffect(() => {
    if (value) {
      setSecVulns(value);
    }
  }, [value, setSecVulns]);

  return {
    value,
    loading,
    error,
  };
};

/**
 * returns true if we need to display the secVulns, based on the data
 * @param secVulns The secVulns.
 */
export function areThereSecVulns(secVulns: UserData): boolean {
  if (secVulns?.data === undefined || secVulns?.data?.getUser === null) {
    return false;
  }
  if (
    secVulns?.data?.getUser?.secvulnSummaryByLogin?.sp1 === '' &&
    secVulns?.data?.getUser?.secvulnSummaryByLogin?.sp2 === '' &&
    secVulns?.data?.getUser?.secvulnSummaryByLogin?.sp3 === '' &&
    secVulns?.data?.getUser?.secvulnSummaryByLogin?.sp4 === ''
  ) {
    return false;
  }
  if (
    parseInt(secVulns?.data?.getUser?.secvulnSummaryByLogin?.sp1, 10) !== 0 ||
    parseInt(secVulns?.data?.getUser?.secvulnSummaryByLogin?.sp2, 10) !== 0 ||
    parseInt(secVulns?.data?.getUser?.secvulnSummaryByLogin?.sp3, 10) !== 0 ||
    parseInt(secVulns?.data?.getUser?.secvulnSummaryByLogin?.sp4, 10) !== 0
  ) {
    return true;
  }
  return false;
}
